// CANCELLING A CHARGE RECORDED BY MISTAKE.
//
// Offered only while NOTHING has been collected against the charge — the server refuses
// otherwise (409 invoice_has_payments), and the row does not show the action. It calls
// the invoice's existing removal on an ACTIVE invoice, which only ever CANCELS: the row is
// kept as a cancelled charge and never deleted, and no screen offers the second, permanent
// step. See server/api/rest/invoices/remove.js.
//
// THE DIALOG STATES EXACTLY WHAT HAPPENS, and only what is true for this charge:
//   always    it leaves the money screen and stops counting in revenue and profit; the
//             record is kept; nothing was collected, so the gap between the partners
//             does not move.
//   a month   the retainer period returns to "לחיוב" and can be billed again.
//   a cost    every client-borne cost it charged returns to "to raise" at its planned
//             price — released in the same transaction as the cancel — and the job the
//             charge converted stays a billing job.
//   the last  a job card with no other charge and nothing waiting leaves the screen.
import { useState } from 'react';
import { invoicesApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import { ErrorText } from './Loader';
import { formatMoney } from './Money';
import ChargeHeader from './ChargeHeader';

const t = he.collections.board;

// A job card whose only content is this charge. A retainer card never leaves — its months
// stay, and this one simply returns to "לחיוב".
const cardLeaves = (card, row) => card.kind === 'job'
  && row.linkedCosts === 0
  && card.charges.filter((c) => c.invoiceId !== row.invoiceId).length === 0
  && card.expectedCharges.length === 0;

export default function CancelChargeModal({ card, row, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await invoicesApi.remove(row.invoiceId);
      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t.cancelTitle}
      onClose={onClose}
      onSave={submit}
      saveLabel={t.cancelConfirm}
      saveDisabled={saving}
    >
      <ChargeHeader card={card} row={row} />
      <ul className="consequences">
        <li>{t.cancelLead(formatMoney(row.amount))}</li>
        {row.periodMonth && <li>{t.cancelMonthBack}</li>}
        {row.linkedCosts > 0 && <li>{t.cancelCostsBack(row.linkedCosts)}</li>}
        {row.linkedCosts > 0 && <li>{t.cancelJobStaysBilling}</li>}
        {cardLeaves(card, row) && <li>{t.cancelCardLeaves}</li>}
      </ul>
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
